import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import {
  getArticlesByCategory,
  getCategory,
  paginate,
  sortOptions,
  timeRangeOptions,
  totalPages,
} from "@/utils/news";
import type { SortType, TimeRange } from "@/types/news";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 4;

export default function CategoryPage() {
  const { categoryName = "" } = useParams();
  const category = getCategory(categoryName);

  const [range, setRange] = useState<TimeRange>("all");
  const [sort, setSort] = useState<SortType>("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta({
    title: category
      ? `51吃瓜网站 - ${category.name}新闻资讯`
      : "51吃瓜网站 - 分类资讯",
    description: category
      ? `浏览51吃瓜网站精选的${category.name}热点新闻和娱乐八卦，掌握最新动态。`
      : "浏览51吃瓜网站精选的热点新闻和娱乐八卦，掌握最新动态。",
    keywords: category
      ? `51吃瓜网站,${category.name},热点新闻,娱乐八卦`
      : "51吃瓜网站,热点新闻,娱乐八卦",
  });

  const list = useMemo(() => {
    if (!category) return [];
    return getArticlesByCategory(category.key, { range, sort });
  }, [category, range, sort]);

  const total = totalPages(list.length, PAGE_SIZE);
  const current = Math.min(page, total);
  const pageItems = paginate(list, current, PAGE_SIZE);

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 py-20 text-center md:px-6">
        <p className="text-5xl">🧐</p>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          没有找到这个分类
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          该分类可能已调整，去看看其他热门分类吧。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  const resetFilters = () => {
    setRange("all");
    setSort("latest");
    setPage(1);
  };

  const handleRange = (value: TimeRange) => {
    setRange(value);
    setPage(1);
  };

  const handleSort = (value: SortType) => {
    setSort(value);
    setPage(1);
  };

  return (
    <div className="bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: category.name }]} />

        <header className="mt-5 overflow-hidden rounded-lg border border-background-200">
          <div className="relative h-40 md:h-52">
            <img
              src={category.cover}
              alt={`${category.name} - 51吃瓜网站分类配图`}
              title={`${category.name} - 51吃瓜网站`}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-center p-5 md:p-8">
              <h1 className="font-heading text-2xl font-black text-background-50 md:text-3xl">
                {category.name}
              </h1>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-background-100">
                {category.description}
              </p>
            </div>
          </div>
        </header>

        <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center justify-between lg:justify-start">
            <p className="text-sm text-foreground-600">
              共 <span className="font-bold text-primary-600">{list.length}</span> 篇资讯
            </p>
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3.5 py-2 text-sm text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line"></i>
              筛选
              <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
          </div>

          <div
            className={`rounded-lg border border-background-200 bg-background-50 p-4 lg:flex lg:items-center lg:gap-6 lg:border-0 lg:p-0 ${
              filterOpen ? "block" : "hidden lg:flex"
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-foreground-500">时间范围</span>
              {timeRangeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleRange(opt.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                    range === opt.value
                      ? "bg-primary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 lg:mt-0">
              <span className="text-xs font-medium text-foreground-500">排序方式</span>
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSort(opt.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                    sort === opt.value
                      ? "bg-accent-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6">
          {pageItems.length > 0 ? (
            <div className="flex flex-col gap-4">
              {pageItems.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <p className="text-4xl">🍉</p>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                当前筛选条件下暂无内容
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                试着放宽时间范围，或重置筛选条件看看全部资讯。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line"></i> 重置筛选
              </button>
            </div>
          )}
        </div>

        <Pagination page={current} total={total} onChange={setPage} />
      </div>
    </div>
  );
}